<script>
import { mapState, mapActions } from 'vuex';
import TwOverlay from '../TwOverlay';
import escapable from '../../mixins/escapable';

export default {
  name: 'AffiliatesLogo',
  components: {
    TwOverlay,
  },
  mixins: [
    escapable,
  ],
  data() {
    return {
      activeTab: 0,
      previewSrc: undefined,
      copyShow: false,
      copyUrlModel: undefined,
      copyUrl: undefined,
    };
  },
  computed: {
    ...mapState({
      affiliate: state => state.affiliates.affiliate.item,
      logo: state => state.affiliates.logo,
    }),
    currentLogo() {
      if (!this.affiliate || !this.affiliate.domains[this.activeTab]) {
        return undefined;
      }
      return this.affiliate.domains[this.activeTab].logo;
    },
    validImage() {
      if (this.previewSrc) {
        return true;
      }
      return !!this.copyUrl;
    },
    availableDomains() {
      const activeDomain = this.affiliate.domains[this.activeTab];
      return this.affiliate.domains.filter((d) => {
        if (d.domainId === activeDomain.domainId) {
          return false;
        }
        return !!d.logo;
      });
    },
    src() {
      if (this.previewSrc) {
        return this.previewSrc;
      }
      if (this.copyUrl) {
        return this.copyUrl;
      }
      if (this.currentLogo) {
        return this.currentLogo;
      }
      return undefined;
    },
  },
  methods: {
    ...mapActions({
      updateLogo: 'affiliates/updateLogo',
    }),
    previewImage(t) {
      const files = t.target.files;
      if (files && files[0]) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.previewSrc = e.target.result;
        };
        reader.readAsDataURL(files[0]);
      }
    },
    copyImage() {
      this.copyUrl = this.copyUrlModel;
      this.copyShow = false;
      this.previewSrc = undefined;
      this.$refs.file.value = '';
    },
    saveImage() {
      const payload = {
        affiliateId: this.affiliate.domains[this.activeTab].affiliateId,
        affiliateDomainId: this.affiliate.domains[this.activeTab].id,
      };
      if (this.$refs.file.value) {
        const formData = new FormData();
        formData.append('logoFile', this.$refs.file.files[0]);
        payload.formData = formData;
      } else {
        payload.formData = { logoPath: this.copyUrl };
      }
      this.updateLogo(payload);
    },
    clearInput() {
      this.$refs.file.value = '';
      this.copyUrlModel = undefined;
      this.copyUrl = undefined;
      this.previewSrc = undefined;
    },
    escapeHandler() {
      this.copyShow = false;
    },
  },
};
</script>

<template>
  <v-flex v-if="affiliate" xs12>
    <v-layout wrap>
      <v-flex xs12 mt-5 mb-2>
        <span class="title">Logo</span>
      </v-flex>
      <v-flex xs12>
        <v-divider/>
      </v-flex>
      <v-flex class="xs12 affiliates-logo__wrapper">
        <tw-overlay :visible="logo.loading"/>
        <v-progress-circular v-if="logo.loading" class="affiliates-logo__wrapper-loading" indeterminate/>
        <div class="affiliates-logo__wrapper-header">
          <v-tabs v-model="activeTab" @change="clearInput">
            <v-tab
              v-for="(domain, index) in affiliate.domains"
              :key="index"
              :class="{ 'error--text': !domain.logo, 'green--text': !!domain.logo }"
            >
              {{ domain.domain.shortName }}
            </v-tab>
          </v-tabs>
        </div>
        <div :class="{ 'grey lighten-3': true }" class="affiliates-logo__wrapper-content">
          <img v-if="src" :src="src" alt="preview" class="affiliates-logo__wrapper-content-img"/>
          <div v-else class="affiliates-logo__wrapper-content-preview grey--text text--lighten-1 title">
            <template v-if="!this.affiliate.domains.length">No domains attached to this affiliate</template>
            <template v-else>Logo Preview</template>
          </div>
          <v-btn
            :disabled="!availableDomains.length"
            color="info"
            class="affiliates-logo__wrapper-content-copy"
            small
            @click="copyShow = true"
          >
            Copy
          </v-btn>
          <v-btn
            :disabled="activeTab === undefined"
            color="info"
            class="affiliates-logo__wrapper-content-upload"
            small
            @click="$refs.file.click()"
          >
            Upload
          </v-btn>
          <v-btn
            :disabled="!validImage"
            color="info"
            class="affiliates-logo__wrapper-content-save"
            small
            @click="saveImage"
          >
            Save
          </v-btn>
          <input
            ref="file"
            type="file"
            class="affiliates-logo__wrapper-file"
            @change="previewImage"
          />
        </div>
        <v-dialog
          v-model="copyShow"
          max-width="400px"
          persistent
          no-click-animation
        >
          <v-card>
            <v-card-title>
              <h1>Copy from other domain</h1>
              <v-spacer/>
              <v-btn icon small @click="copyShow = false">
                <v-icon small>close</v-icon>
              </v-btn>
            </v-card-title>
            <v-divider/>
            <v-card-text>
              <v-autocomplete
                v-model="copyUrlModel"
                :items="availableDomains"
                placeholder="Select domain"
                item-text="domain.name"
                item-value="logo"
              />
            </v-card-text>
            <v-card-actions>
              <v-spacer/>
              <v-btn :disabled="!copyUrlModel" color="info" @click="copyImage">Copy</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-flex>
    </v-layout>
  </v-flex>
</template>

<style lang="scss">
.affiliates-logo__wrapper {
  position: relative;

  &-loading {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 100;
  }

  &-content {
    height: 200px;
    width: 100%;
    text-align: center;
    position: relative;
    align-content: center;
    justify-content: center;
    display: flex;

    &-preview {
      padding-top: 90px;
    }

    &-img {
      max-width: 100%;
      max-height: 200px;
      margin: 0 auto;
      object-fit: contain;
    }

    &-copy {
      position: absolute !important;
      bottom: 120px;
      right: 0;
    }

    &-upload {
      position: absolute !important;
      bottom: 80px;
      right: 0;
    }

    &-save {
      position: absolute !important;
      bottom: 40px;
      right: 0;
    }
  }

  &-file {
    display: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Affiliates/AffiliatesLogo.vue